import React from "react";
import { Button } from "@headlessui/react";
import { useDarkMode } from "../../contexts/darkModeContext";

const DEFAULT_TRAIL_COUNT = 5;

const DottedArrowIcon = ({ className = "", ...props }) => (
  <svg
    aria-hidden="true"
    className={className}
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    {[
      [11, 8], [19, 8], [19, 15], [27, 15], [27, 22], [35, 22],
      [27, 29], [19, 29], [19, 36], [11, 36], [15, 22],
    ].map(([cx, cy], index) => (
      <circle key={index} cx={cx} cy={cy} r="3.2" fill="currentColor" />
    ))}
  </svg>
);

/**
 * Split CTA button with a bright icon tile that expands into an animated trail.
 */
const AnimatedTrailButton = ({
  label,
  onClick,
  icon = DottedArrowIcon,
  trailCount = DEFAULT_TRAIL_COUNT,
  accentColor = "#16a34a",
  surfaceColor,
  textColor,
  className = "",
  disabled = false,
  type = "button",
  "aria-label": ariaLabel,
}) => {
  const { darkMode } = useDarkMode();
  const safeTrailCount = Math.max(3, Math.min(8, trailCount));
  const resolvedSurfaceColor = surfaceColor || (darkMode ? "rgb(255 255 255 / 0.05)" : "rgb(0 0 0 / 0.05)");
  const resolvedTextColor = textColor || (darkMode ? "#ffffff" : "#111827");
  const renderIcon = (className, style) => {
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon, {
        className: `${icon.props.className || ""} ${className}`.trim(),
        style: { ...icon.props.style, ...style },
        "aria-hidden": true,
      });
    }

    const Icon = icon;
    return <Icon className={className} style={style} aria-hidden="true" />;
  };

  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || label}
      className={`
        group relative isolate inline-flex w-fit min-h-9 items-center
        overflow-hidden rounded-lg border-0 p-1
        cursor-pointer bg-[var(--trail-surface)] text-[var(--trail-text)]
        shadow-[0_0.2rem_0_rgb(15_23_42_/_0.22),0_0.65rem_1rem_-0.7rem_rgb(15_23_42_/_0.35)]
        transition-all duration-200 ease-out
        hover:-translate-y-0.5
        hover:shadow-[0_0.15rem_0_rgb(15_23_42_/_0.25),0_0.8rem_1.1rem_-0.7rem_rgb(15_23_42_/_0.45)]
        active:translate-y-0.5
        active:shadow-[0_0.05rem_0_rgb(15_23_42_/_0.2),0_0.4rem_0.7rem_-0.5rem_rgb(15_23_42_/_0.35)]
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
        focus-visible:outline-[var(--trail-accent)]
        disabled:cursor-not-allowed disabled:opacity-50 disabled:transform-none
        motion-reduce:transition-none
        ${className}
      `}
      style={{
        "--trail-accent": accentColor,
        "--trail-surface": resolvedSurfaceColor,
        "--trail-text": resolvedTextColor,
      }}
    >
      <span
        className="absolute inset-y-1 left-1 -z-10 w-8 rounded-md
          bg-[var(--trail-accent)] transition-all duration-300
          ease-[cubic-bezier(0.65,0,0.2,1)]
          group-hover:right-1 group-hover:w-[calc(100%-0.5rem)]
          group-focus-visible:right-1 group-focus-visible:w-[calc(100%-0.5rem)]
          motion-reduce:transition-none"
        aria-hidden="true"
      />

      <span
        className="relative z-10 grid min-h-8 w-8 shrink-0 place-items-center rounded-md
          bg-[var(--trail-accent)] text-white transition-all duration-200
          group-hover:translate-x-1 group-hover:opacity-0
          group-focus-visible:translate-x-1 group-focus-visible:opacity-0
          motion-reduce:transition-none"
        aria-hidden="true"
      >
        {renderIcon("size-5 stroke-2")}
      </span>

      <span className="relative z-10 self-center whitespace-nowrap px-2.5 text-xs font-semibold tracking-tight
        transition-all duration-200 group-hover:translate-x-1 group-hover:opacity-0
        group-focus-visible:translate-x-1 group-focus-visible:opacity-0
        motion-reduce:transition-none">
        {label}
      </span>

      <span
        className="pointer-events-none absolute inset-y-1 inset-x-2 z-20
          flex items-center justify-around text-white opacity-0
          transition-opacity delay-[220ms] duration-150 group-hover:opacity-100
          group-focus-visible:opacity-100 motion-reduce:transition-none"
        aria-hidden="true"
      >
        {Array.from({ length: safeTrailCount }, (_, index) => (
          <React.Fragment key={index}>
            {renderIcon(
              "size-5 stroke-2 opacity-30 group-hover:animate-pulse group-focus-visible:animate-pulse motion-reduce:animate-none",
              { animationDelay: `${index * 85}ms` },
            )}
          </React.Fragment>
        ))}
      </span>
    </Button>
  );
};

export { DottedArrowIcon };
export default AnimatedTrailButton;
